import React, { useEffect, useState, useRef } from "react"
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
} from "@heroicons/react/24/outline"
import moment from "moment"
import VariantReader from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"

// MonthSelector Component
// The month / year panel. It takes the place of the calendar inside the
// dialog — it is the dialog's content while it is open, not a layer over it,
// so it sizes the dialog the way the calendar does.
const MonthSelector = ({ viewDate, onSelect, onClose, variants }) => {
  const [showMonths, setShowMonths] = useState(true)
  const currentYear = moment().year()
  const yearsRange = Array.from({ length: 15 }, (_, i) => currentYear - 7 + i)

  return (
    <div className={variants?.monthSelector?.container || "bg-admin_paper rounded-[28px] p-6"}>
      {/* Header */}
      <div
        className={
          variants?.monthSelector?.header?.wrapper ||
          "flex items-center justify-between mb-4"
        }
      >
        <span
          className={
            variants?.monthSelector?.header?.format || "text-admin_text/60 text-sm"
          }
        >
          {viewDate.format("MMMM YYYY")}
        </span>
        <button onClick={onClose}>
          <XMarkIcon
            className={
              variants?.monthSelector?.header?.closeButton ||
              "w-6 h-6 text-admin_text/60 cursor-pointer"
            }
          />
        </button>
      </div>

      {/* Month/Year Selection */}
      <div
        className={
          variants?.monthSelector?.selectors?.wrapper || "flex gap-4 mb-6"
        }
      >
        {/* Month Dropdown */}
        <div className="relative flex-1">
          <button
            onClick={() => setShowMonths(true)}
            className={twMerge(
              variants?.monthSelector?.selectors?.button?.base ||
                "w-full flex items-center justify-between px-4 py-2 rounded-lg border border-admin_bordered",
              showMonths
                ? variants?.monthSelector?.selectors?.button?.active ||
                    "bg-text/10"
                : variants?.monthSelector?.selectors?.button?.hover ||
                    "hover:bg-text/5"
            )}
            style={{ height: "48px" }}
          >
            <span>{viewDate.format("MMM")}</span>
            <ChevronDownIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Year Selection */}
        <div className="flex-1">
          <button
            onClick={() => setShowMonths(false)}
            className={twMerge(
              variants?.monthSelector?.selectors?.button?.base ||
                "w-full flex items-center justify-between px-4 py-2 rounded-lg border border-admin_bordered",
              !showMonths
                ? variants?.monthSelector?.selectors?.button?.active ||
                    "bg-text/10"
                : variants?.monthSelector?.selectors?.button?.hover ||
                    "hover:bg-text/5"
            )}
            style={{ height: "48px" }}
          >
            <span>{viewDate.format("YYYY")}</span>
            <ChevronDownIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Scrollable Container: the list is what scrolls, never the dialog */}
      <div className="overflow-y-auto max-h-[min(480px,50vh)] scrollbar-thin scrollbar-thumb-text/10 scrollbar-track-transparent">
        {showMonths ? (
          /* Months List */
          <div
            className={
              variants?.monthSelector?.monthList?.wrapper || "space-y-2"
            }
          >
            {moment.months().map((month, index) => {
              const isSelected = index === viewDate.month()
              return (
                <button
                  key={month}
                  onClick={() => onSelect({ type: "month", value: index })}
                  className={twMerge(
                    variants?.monthSelector?.monthList?.item?.base ||
                      "w-full px-4 py-3 rounded-lg text-left flex items-center",
                    isSelected
                      ? variants?.monthSelector?.monthList?.item?.selected ||
                          "bg-primary/10"
                      : variants?.monthSelector?.monthList?.item?.hover ||
                          "hover:bg-text/5"
                  )}
                >
                  {month}
                </button>
              )
            })}
          </div>
        ) : (
          /* Years List */
          <div
            className={
              variants?.monthSelector?.monthList?.wrapper || "space-y-2"
            }
          >
            {yearsRange.map((year) => {
              const isSelected = year === viewDate.year()
              return (
                <button
                  key={year}
                  onClick={() => {
                    onSelect({ type: "year", value: year })
                    // a year is picked to reach a month: the list turns back to
                    // the months rather than closing the panel
                    setShowMonths(true)
                  }}
                  className={twMerge(
                    variants?.monthSelector?.monthList?.item?.base ||
                      "w-full px-4 py-3 rounded-lg text-left flex items-center",
                    isSelected
                      ? variants?.monthSelector?.monthList?.item?.selected ||
                          "bg-primary/10"
                      : variants?.monthSelector?.monthList?.item?.hover ||
                          "hover:bg-text/5"
                  )}
                >
                  {year}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const DatePicker = ({
  value,
  onChange,
  range = false,
  variant = "default",
  styleOverrides = {},
  dateFormat = "DD.MM.YY",
  monthFormat = "MMMM YYYY",
  showToday = true,
  // What one pick is: a day, a whole month or a whole year. Alone, a month or
  // a year is picked and emitted as its first day; with `range`, the two ends
  // are whole units too, and the range emitted runs from the first moment of
  // the first to the last moment of the last ({ startDate, endDate }).
  granularity = "day",     // "day" | "month" | "year"
  unitFormats = {},        // { month, year } — how a picked month / year reads
  open,                    // optional controlled open state
  onOpenChange,            // optional controlled open setter
  trigger,                 // optional node replacing the default input
  triggerClassName = "inline-flex cursor-pointer", // the box the trigger sits in
}) => {
  // Use variant system
  const variants = VariantReader.useVariant({
    componentName: "DatePicker",
    variant,
    styleOverrides,
  })

  // Use ref to track previous value for comparison
  const prevValueRef = useRef(null)

  // Initialize dates with state initializer function
  const [dates, setDates] = useState(() => {
    const initialStartDate = value?.startDate
      ? moment(value.startDate)
      : value
      ? moment(value)
      : null
    const initialEndDate = value?.endDate
      ? moment(value.endDate)
      : range
      ? null
      : initialStartDate

    return {
      start: initialStartDate && initialStartDate.isValid() ? initialStartDate : null,
      end: initialEndDate && initialEndDate.isValid() ? initialEndDate : null,
    }
  })

  const [isOpenState, setIsOpenState] = useState(false)
  const isOpen = open !== undefined ? open : isOpenState
  const setIsOpen = (v) => {
    // every opening starts on the start; what follows is the user's to steer
    if (v) setSelectionStep("start")
    onOpenChange?.(v)
    if (open === undefined) setIsOpenState(v)
  }
  const [showMonthSelector, setShowMonthSelector] = useState(false)

  // a month / year is the unit, not a day: the grid is of those, and a picked
  // one is written the way the unit reads
  const byUnit = granularity === "month" || granularity === "year"
  const format = (date) => {
    if (granularity === "month") return date.format(unitFormats.month || "MMM YYYY")
    if (granularity === "year") return date.format(unitFormats.year || "YYYY")
    return date.format(dateFormat)
  }

  // single-select a whole month / year (granularity modes) → emit + close
  const selectPeriod = (date) => {
    setDates({ start: date.clone(), end: date.clone() })
    setViewDate(date.clone())
    onChange?.(date.toDate())
    setIsOpen(false)
  }
  const [hoverDate, setHoverDate] = useState(null)
  // Which end the calendar edits. The dialog opens on the start, and from
  // there the focus is the user's: picking the start moves on to the end
  // (the one step that saves a click), and nothing moves it after that —
  // the header's two slots do.
  const [selectionStep, setSelectionStep] = useState("start")
  const [viewDate, setViewDate] = useState(() => {
    const startDate = value?.startDate
      ? moment(value.startDate)
      : value
      ? moment(value)
      : moment()
    return startDate.isValid() ? startDate : moment()
  })

  // Compare values for equality
  const areValuesEqual = (val1, val2) => {
    if (val1 === val2) return true
    if (!val1 && !val2) return true
    if (!val1 || !val2) return false

    // Handle object format {startDate, endDate}
    if (val1.startDate && val2.startDate) {
      const startEqual = moment(val1.startDate).isSame(
        moment(val2.startDate),
        "day"
      )
      const endEqual =
        val1.endDate && val2.endDate
          ? moment(val1.endDate).isSame(moment(val2.endDate), "day")
          : val1.endDate === val2.endDate
      return startEqual && endEqual
    }

    // Handle direct date values - ensure valid moments before comparison
    const moment1 = moment(val1)
    const moment2 = moment(val2)
    
    if (!moment1.isValid() || !moment2.isValid()) return false
    
    return moment1.isSame(moment2, "day")
  }

  // Handle external value changes
  useEffect(() => {
    // Skip if value hasn't changed
    if (areValuesEqual(value, prevValueRef.current)) {
      return
    }

    const newStartDate = value?.startDate
      ? moment(value.startDate)
      : value
      ? moment(value)
      : null

    const newEndDate = value?.endDate
      ? moment(value.endDate)
      : range
      ? null
      : newStartDate

    // Only check for changes if we have valid dates
    const startChanged = newStartDate && newStartDate.isValid() && (!dates.start || !newStartDate.isSame(dates.start, "day"))
    const endChanged = range && newEndDate && newEndDate.isValid() &&
      (!dates.end || !newEndDate.isSame(dates.end, "day"))

    if (startChanged || endChanged || !prevValueRef.current) {
      setDates({
        start: newStartDate && newStartDate.isValid() ? newStartDate : dates.start,
        end: newEndDate && newEndDate.isValid() ? newEndDate : dates.end,
      })
      if (newStartDate && newStartDate.isValid()) {
        setViewDate(newStartDate)
      }
    }

    prevValueRef.current = value
  }, [value, range])

  /**
   * A click on the calendar writes the end the header has focused — the Start
   * slot or the End slot — and leaves the other one alone, so either can be
   * changed on its own without the range being started over. Picking a start
   * that is past the end (or an end before the start) turns the two around
   * rather than dropping one.
   */
  const handleDateSelect = (date) => {
    if (!range) {
      setDates({ start: date, end: null })
      // Don't call onChange here - wait for OK button
      return
    }

    setHoverDate(null)

    // the end: it stays focused, so the same click can correct it
    if (selectionStep === "end") {
      if (dates.start && date.isBefore(dates.start, "day")) {
        setDates({ start: date, end: dates.start })
        return
      }
      setDates((prev) => ({ ...prev, end: date }))
      return
    }

    // the start: an end it passes becomes the start instead
    if (dates.end && date.isAfter(dates.end, "day")) {
      setDates({ start: dates.end, end: date })
      return
    }

    setDates((prev) => ({ ...prev, start: date }))
    // the one step the picker takes on its own: from the start to the end
    setSelectionStep("end")
  }

  /** Focus one end of the range: the calendar then edits that one. */
  const focusRangeEnd = (step) => {
    setSelectionStep(step)
    setHoverDate(null)
    const date = step === "start" ? dates.start : dates.end
    if (date && date.isValid()) setViewDate(date.clone())
  }

  const handleTodayClick = () => {
    const today = moment()
    if (!range) {
      setDates({ start: today, end: null })
      onChange?.(today.toDate())
    } else {
      setDates({ start: today, end: null })
      setSelectionStep("end")
    }
    setViewDate(today)
  }

  /**
   * The band the grid paints: the two ends in order, with the day under the
   * mouse standing in for whichever end the header has focused. Nothing else
   * changes it, so the colours only follow the focused end and a hover left
   * over from a finished range cannot repaint the band.
   */
  const ordered = (a, b) => (a.isAfter(b, "day") ? { from: b, to: a } : { from: a, to: b })

  const band = () => {
    if (!range) return null
    if (hoverDate) {
      if (selectionStep === "end" && dates.start) return ordered(dates.start, hoverDate)
      if (selectionStep === "start" && dates.end) return ordered(hoverDate, dates.end)
    }
    if (dates.start && dates.end) return ordered(dates.start, dates.end)
    // one end picked and the other still open: that day alone is the band
    if (dates.start) return { from: dates.start, to: dates.start }
    if (dates.end) return { from: dates.end, to: dates.end }
    return null
  }

  const isInRange = (date) => {
    const painted = band()
    return !!painted && date.isBetween(painted.from, painted.to, "day", "[]")
  }

  const isRangeStart = (date) => dates.start && date.isSame(dates.start, "day")
  const isRangeEnd = (date) => dates.end && date.isSame(dates.end, "day")

  const handleDateSelection = ({ type, value: selectedValue }) => {
    const newDate = viewDate.clone()
    if (type === "month") {
      newDate.month(selectedValue)
    } else if (type === "year") {
      newDate.year(selectedValue)
    }
    setViewDate(newDate)
    // the month is the last thing picked here; a year only moves the list
    if (type === "month") setShowMonthSelector(false)
  }

  // Always six weeks: a month spans five or six of them, and a grid that
  // changes its row count moves everything under it (and the dialog itself)
  // as the month is stepped. The extra row is days of the next month, shown
  // the way the leading ones are.
  const WEEKS = 6

  const generateDays = () => {
    const days = []
    const day = viewDate.clone().startOf("month").startOf("week")

    for (let i = 0; i < WEEKS * 7; i++) {
      days.push(day.clone())
      day.add(1, "day")
    }
    return days
  }

  const getRangeStyle = (date) => {
    const painted = band()
    if (!painted) return ""
    const isFrom = date.isSame(painted.from, "day")
    const isTo = date.isSame(painted.to, "day")
    if (!isFrom && !isTo && !isInRange(date)) return ""

    // the band wears the theme's selected colour, ends rounded
    const classes = twMerge(
      "rounded-none",
      variants?.calendar?.dayButton?.selected || "bg-primary text-text_inv"
    )
    if (isFrom && isTo) return `${classes} rounded-full`
    if (isFrom) return `${classes} rounded-l-full`
    if (isTo) return `${classes} rounded-r-full`
    return classes
  }

  /**
   * The room a whole value needs, in characters of the (tabular) digits it is
   * written in: a full range in the given format, or one date when the picker
   * takes one. The control is given it up front, so it does not grow as the
   * dates are picked and shrink back to "Select dates".
   */
  const valueWidth = () => {
    const sample = format(moment("2026-12-28"))
    const text = range ? `${sample} - ${sample}` : sample
    return `${text.length}ch`
  }

  const formatDisplayDate = () => {
    if (!range) {
      return (dates.start && dates.start.isValid())
        ? format(dates.start)
        : "Select date"
    }
    // an end of the range that is not picked yet is a dash, not a sentence:
    // the text keeps its shape while the range is being made
    const start = dates.start && dates.start.isValid() ? format(dates.start) : "—"
    const end = dates.end && dates.end.isValid() ? format(dates.end) : "—"
    if (start === "—" && end === "—") return "Select dates"
    return `${start} - ${end}`
  }

  /**
   * The months of a year, or twelve years, to pick from. Alone, a click picks
   * the unit and closes; in a range it writes the focused end, as a day does,
   * and the band between the ends is painted the same way.
   */
  const unitGrid = () => {
    const isMonth = granularity === "month"
    const base = viewDate.year() - (viewDate.year() % 12)
    const cells = isMonth
      ? moment.monthsShort().map((label, i) => ({ label, date: viewDate.clone().month(i).startOf("month") }))
      : Array.from({ length: 12 }, (_, i) => ({ label: String(base + i), date: viewDate.clone().year(base + i).startOf("year") }))
    const stepBy = isMonth ? 1 : 12
    const painted = band()
    const stepClass = variants?.header?.monthStep || "w-9 h-9 rounded-full hover:bg-text/10 flex items-center justify-center text-admin_text"

    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <button aria-label="Previous" onClick={() => setViewDate(viewDate.clone().subtract(stepBy, "year"))} className={stepClass}>
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <span className="text-admin_text text-base font-semibold">{isMonth ? viewDate.format("YYYY") : `${base}–${base + 11}`}</span>
          <button aria-label="Next" onClick={() => setViewDate(viewDate.clone().add(stepBy, "year"))} className={stepClass}>
            <ChevronRightIcon className="w-5 h-5" />
          </button>
          {!range && (
            <XMarkIcon className={variants?.header?.closeButton || "w-6 h-6 text-admin_text/60 cursor-pointer"} onClick={() => setIsOpen(false)} />
          )}
        </div>
        <div className="grid grid-cols-3 gap-y-2" onMouseLeave={() => setHoverDate(null)}>
          {cells.map(({ label, date }) => {
            const end = range
              ? painted && (date.isSame(painted.from, granularity) || date.isSame(painted.to, granularity))
              : dates.start && dates.start.isValid() && date.isSame(dates.start, granularity)
            const inside = range && painted && !end && date.isBetween(painted.from, painted.to, granularity, "()")
            return (
              <button
                key={label}
                type="button"
                onClick={() => (range ? handleDateSelect(date) : selectPeriod(date))}
                onMouseEnter={() => range && setHoverDate(date)}
                className={twMerge(
                  "h-11 rounded-xl text-sm text-admin_text hover:bg-primary/10",
                  inside && (variants?.calendar?.dayButton?.inRange || "bg-primary/20"),
                  end && twMerge(variants?.calendar?.dayButton?.selected || "bg-primary text-text_inv", "hover:bg-primary")
                )}
              >
                {label}
              </button>
            )
          })}
        </div>
      </>
    )
  }

  return (
    <div className={variants?.container?.wrapper || "relative"}>
      {trigger ? (
        <span onClick={() => setIsOpen(true)} className={triggerClassName}>{trigger}</span>
      ) : (
        <div
          onClick={() => setIsOpen(true)}
          className={
            variants?.input?.wrapper ||
            "cursor-pointer p-4 bg-admin_paper rounded-lg border border-admin_bordered"
          }
        >
          <div
            className={
              variants?.input?.label || "text-sm text-admin_text/60 mb-1"
            }
          >
            Select date{range ? "s" : ""}
          </div>
          {/* the closed control reads the value: digits are tabular and the
              line keeps its height, so the toolbar around it does not move
              as the range is picked */}
          <div
            className={twMerge(
              variants?.input?.value || "text-sm text-admin_text",
              "tabular-nums whitespace-nowrap overflow-hidden text-ellipsis min-h-[1.4em]"
            )}
            style={{ minWidth: valueWidth() }}
          >
            {formatDisplayDate()}
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className={
            variants?.container?.modal ||
            "fixed inset-0 bg-theme_black/50 flex items-center justify-center z-50"
          }
        >
          <div
            className={
              variants?.container?.mainContainer ||
              "bg-admin_paper rounded-[28px] max-w-[360px] w-full shadow-xl relative"
            }
          >
            {byUnit && !range ? (
              <div className="p-6">{unitGrid()}</div>
            ) : showMonthSelector ? (
              <MonthSelector
                viewDate={viewDate}
                onSelect={handleDateSelection}
                onClose={() => setShowMonthSelector(false)}
                variants={variants}
              />
            ) : (
              <>
                <div className={variants?.container?.header || "px-6 pt-6"}>
                  {/* the line below says what is picked; a title repeating it
                      only takes the room */}
                  <div className="flex items-center justify-end mb-4">
                    <XMarkIcon
                      className={
                        variants?.header?.closeButton ||
                        "w-6 h-6 text-admin_text/60 cursor-pointer"
                      }
                      onClick={() => setIsOpen(false)}
                    />
                  </div>

                  {range ? (
                    // The two ends of the range, each in a slot of its own.
                    // A slot keeps its width and height whether its date is
                    // picked or not — the label says which end it is, so an
                    // empty one needs no words, and nothing moves as the
                    // range is made.
                    <div className={variants?.header?.rangeRow || "flex items-stretch gap-3 mb-4"}>
                      {[
                        { label: "Start", date: dates.start, step: "start" },
                        { label: "End", date: dates.end, step: "end" },
                      ].map(({ label, date, step }) => (
                        <button
                          key={label}
                          type="button"
                          aria-pressed={selectionStep === step}
                          onClick={() => focusRangeEnd(step)}
                          className={twMerge(
                            variants?.header?.rangeSlot ||
                              "flex-1 min-w-0 rounded-xl px-3 py-2 border border-admin_bordered text-left",
                            "cursor-pointer",
                            selectionStep === step &&
                              (variants?.header?.rangeSlotActive || "border-admin_primary bg-admin_primary/5")
                          )}
                        >
                          <div className={variants?.header?.rangeLabel || "text-[11px] uppercase tracking-wide text-admin_text/50"}>
                            {label}
                          </div>
                          <div
                            className={twMerge(
                              variants?.header?.rangeValue ||
                                "text-[17px] leading-tight text-admin_text font-normal",
                              "tabular-nums whitespace-nowrap overflow-hidden text-ellipsis min-h-[1.3em]",
                              !(date && date.isValid()) && "text-admin_text/30"
                            )}
                          >
                            {date && date.isValid() ? format(date) : "—"}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div
                      className={twMerge(
                        variants?.header?.selectedDate ||
                          "text-[32px] text-admin_text font-normal mb-4",
                        // the text changes with every click: the line keeps
                        // its height so nothing below it moves
                        "tabular-nums min-h-[1.2em] whitespace-nowrap overflow-hidden text-ellipsis"
                      )}
                    >
                      {formatDisplayDate()}
                    </div>
                  )}

                  {/* the month the grid shows: stepped with the chevrons
                      beside it, or picked from the month / year panel */}
                  {!byUnit && (
                  <div className={variants?.header?.monthRow || "flex items-center justify-center gap-1 mb-4"}>
                    <button
                      aria-label="Previous month"
                      onClick={() => setViewDate(viewDate.clone().subtract(1, "month"))}
                      className={variants?.header?.monthStep || "w-9 h-9 rounded-full hover:bg-text/10 flex items-center justify-center text-admin_text"}
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setShowMonthSelector(true)}
                      className={
                        variants?.header?.monthSelector ||
                        "text-admin_text hover:bg-text/10 px-4 py-2 rounded-lg flex items-center"
                      }
                    >
                      {viewDate.format(monthFormat)}
                    </button>
                    <button
                      aria-label="Next month"
                      onClick={() => setViewDate(viewDate.clone().add(1, "month"))}
                      className={variants?.header?.monthStep || "w-9 h-9 rounded-full hover:bg-text/10 flex items-center justify-center text-admin_text"}
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                  )}
                </div>

                {byUnit ? (
                  <div className={variants?.container?.body || "px-6 pb-6"}>{unitGrid()}</div>
                ) : (
                <div className={variants?.container?.body || "px-6 pb-6"}>
                  <div className="grid grid-cols-7 mb-2">
                    {moment.weekdaysMin().map((day) => (
                      <div
                        key={day}
                        className={
                          variants?.calendar?.weekdayHeader ||
                          "text-center text-sm py-2 text-admin_text/60"
                        }
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  <div
                    className={
                      variants?.calendar?.daysGrid || "grid grid-cols-7"
                    }
                    onMouseLeave={() => setHoverDate(null)}
                  >
                    {generateDays().map((date, idx) => {
                      const isSelected = range 
                        ? isRangeStart(date) || isRangeEnd(date)
                        : dates.start && date.isSame(dates.start, "day")
                      const isToday = date.isSame(moment(), "day")
                      const isCurrentMonth = date.isSame(viewDate, "month")
                      
                      return (
                        <button
                          key={date.format("YYYY-MM-DD")}
                          onClick={() => handleDateSelect(date)}
                          onMouseEnter={() => range && setHoverDate(date)}
                          className={twMerge(
                            variants?.calendar?.dayButton?.base ||
                              "relative aspect-square flex items-center justify-center text-sm text-admin_text rounded-full hover:bg-primary",
                            // Override hover for selected dates in single mode to prevent text invisibility
                            isSelected && !range && "hover:bg-admin_primary/10",
                            !isCurrentMonth &&
                              (variants?.calendar?.dayButton?.otherMonth ||
                                "text-admin_text/40"),
                            getRangeStyle(date)
                          )}
                        >
                          <div
                            className={twMerge(
                              variants?.calendar?.dayButton?.dayWrapper ||
                                "w-10 h-10 flex items-center justify-center rounded-full",
                              isSelected &&
                                !range &&
                                "!border !border-admin_primary !text-admin_primary",
                              isToday &&
                                !isSelected &&
                                !isInRange(date) &&
                                (variants?.calendar?.dayButton?.today ||
                                  "font-bold text-admin_primary")
                            )}
                          >
                            {date.format("D")}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
                )}

                <div
                  className={
                    variants?.container?.footer ||
                    "flex items-center justify-between p-4 border-t border-text/10"
                  }
                >
                  {/* the footer's start: Today, or nothing — OK keeps its place */}
                  {showToday && !byUnit ? (
                    <button
                      onClick={handleTodayClick}
                      className={twMerge(
                        variants?.buttons?.base || "px-6 py-2 rounded-full",
                        variants?.buttons?.secondary ||
                          "text-primary hover:bg-text/10"
                      )}
                    >
                      Today
                    </button>
                  ) : <span />}

                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        if (!range && dates.start && dates.start.isValid()) {
                          const dateValue = dates.start.toDate()
                          onChange?.(dateValue)
                          setIsOpen(false)
                          setSelectionStep("start")
                        } else if (range && dates.start && dates.start.isValid() && dates.end && dates.end.isValid()) {
                          const rangeValue = byUnit
                            ? {
                                startDate: dates.start.clone().startOf(granularity).toDate(),
                                endDate: dates.end.clone().endOf(granularity).toDate(),
                              }
                            : {
                                startDate: dates.start.toDate(),
                                endDate: dates.end.toDate(),
                              }
                          onChange?.(rangeValue)
                          setIsOpen(false)
                          setSelectionStep("start")
                        }
                      }}
                      disabled={
                        !range
                          ? !dates.start || !dates.start.isValid()
                          : !dates.start || !dates.start.isValid() || !dates.end || !dates.end.isValid()
                      }
                      className={twMerge(
                        variants?.buttons?.base || "px-6 py-2 rounded-full",
                        variants?.buttons?.primary ||
                          "text-primary hover:bg-primary/10",
                        (!range ? !dates.start || !dates.start.isValid() : !dates.start || !dates.start.isValid() || !dates.end || !dates.end.isValid())
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      )}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DatePicker
